import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Icon, LatLng } from 'leaflet';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import { MyLocation, Clear } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';

const STORAGE_KEY = 'developer_settings';

export interface DeveloperLocation {
  enabled: boolean;
  latitude: number;
  longitude: number;
}

const defaultSettings: DeveloperLocation = {
  enabled: false,
  latitude: 34.0522,
  longitude: -118.2437,
};

const developerIcon = new Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export function getDeveloperLocation(): DeveloperLocation | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const settings = JSON.parse(stored) as DeveloperLocation;
      if (settings.enabled) {
        return settings;
      }
    }
  } catch (error) {
    console.error('Failed to read developer settings:', error);
  }
  return null;
}

interface LocationPickerProps {
  position: LatLng | null;
  onPositionChange: (latlng: LatLng) => void;
}

function LocationPicker({ position, onPositionChange }: LocationPickerProps) {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng);
    },
  });

  return position ? <Marker position={position} icon={developerIcon} /> : null;
}

export function DeveloperSettings() {
  const [settings, setSettings] = useState<DeveloperLocation>(defaultSettings);
  const [markerPosition, setMarkerPosition] = useState<LatLng | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as DeveloperLocation;
        setSettings(parsed);
        if (parsed.latitude && parsed.longitude) {
          setMarkerPosition(new LatLng(parsed.latitude, parsed.longitude));
        }
      }
    } catch (error) {
      console.error('Failed to load developer settings:', error);
    }
  }, []);

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSettings = { ...settings, enabled: event.target.checked };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const handlePositionChange = (latlng: LatLng) => {
    setMarkerPosition(latlng);
    const newSettings = {
      ...settings,
      latitude: latlng.lat,
      longitude: latlng.lng,
    };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const handleClear = () => {
    setMarkerPosition(null);
    const newSettings = {
      ...settings,
      enabled: false,
      latitude: defaultSettings.latitude,
      longitude: defaultSettings.longitude,
    };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const saveSettings = (newSettings: DeveloperLocation) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save developer settings:', error);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Developer Settings
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Location Override
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable this to use a custom location for share creation instead of your actual location.
            Click on the map to set the location.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                onChange={handleToggle}
                color="primary"
              />
            }
            label="Use developer location"
          />

          {settings.enabled && markerPosition && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<MyLocation />}
                label={`${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}`}
                color="success"
              />
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleClear}
                color="error"
              >
                Clear
              </Button>
            </Box>
          )}

          {saved && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Settings saved
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Set Location
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click anywhere on the map to set the developer location.
          </Typography>

          <Box sx={{ height: { xs: 300, md: 400 }, borderRadius: 1, overflow: 'hidden' }}>
            <MapContainer
              center={[settings.latitude, settings.longitude]}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationPicker
                position={markerPosition}
                onPositionChange={handlePositionChange}
              />
            </MapContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
